import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { products } from '../data/Products';
import {
    FilePdf, WhatsappLogo, ArrowLeft, Info, MagnifyingGlassPlus, X,
    Funnel, Buildings, Tag, Trash, MagnifyingGlass,
    CaretDown, ShareNetwork, ArrowUp
} from "@phosphor-icons/react";

// SEGURIDAD: Extraemos el número con un valor de respaldo (Fallback) para evitar "undefined"
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "522282425769";

const ProductList = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [activeBrand, setActiveBrand] = useState("Todas");
    const [activeSubcat, setActiveSubcat] = useState("Todas");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [lastVisitedId, setLastVisitedId] = useState(null);

    const [isBrandsExpanded, setIsBrandsExpanded] = useState(true);
    const [isCatsExpanded, setIsCatsExpanded] = useState(true);

    useEffect(() => {
        const handleScroll = () => setShowBackToTop(window.scrollY > 400);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const savedScroll = sessionStorage.getItem('catalogueScrollPos');
        const savedId = sessionStorage.getItem('lastProductId');

        if (savedId) setLastVisitedId(savedId);

        if (savedScroll) {
            window.scrollTo(0, parseInt(savedScroll));
            const timer = setTimeout(() => {
                setLastVisitedId(null);
                sessionStorage.removeItem('lastProductId');
                sessionStorage.removeItem('catalogueScrollPos');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleProductClick = (id) => {
        sessionStorage.setItem('catalogueScrollPos', window.scrollY.toString());
        sessionStorage.setItem('lastProductId', id);
    };

    useEffect(() => {
        document.body.style.overflow = isFilterOpen ? 'hidden' : 'unset';
    }, [isFilterOpen]);

    const filteredByBrand = useMemo(() => {
        return products.filter(p => activeBrand === "Todas" || p.brand === activeBrand);
    }, [activeBrand]);

    const filteredProducts = useMemo(() => {
        return filteredByBrand.filter(p => {
            const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.id.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesSub = activeSubcat === "Todas" || p.subcategory === activeSubcat;
            return matchesSearch && matchesSub;
        });
    }, [searchTerm, filteredByBrand, activeSubcat]);

    const brands = ["Todas", ...new Set(products.map(p => p.brand))];
    const categoriesByBrand = ["Todas", ...new Set(filteredByBrand.map(p => p.subcategory))];

    const handleBrandClick = (brand) => {
        setActiveBrand(activeBrand === brand ? "Todas" : brand);
        setActiveSubcat("Todas");
        if(window.innerWidth < 1024 && activeBrand !== brand) setIsFilterOpen(false);
    };

    const handleSubcatClick = (sub) => {
        setActiveSubcat(activeSubcat === sub ? "Todas" : sub);
        if(window.innerWidth < 1024 && activeSubcat !== sub) setIsFilterOpen(false);
    };

    const FilterContent = () => (
        <div className="bg-white rounded-3xl h-full lg:h-auto lg:shadow-sm lg:border lg:border-gray-100 flex flex-col lg:max-h-[calc(100vh-160px)] relative overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center bg-white z-10">
                <h3 className="flex items-center gap-2 font-bold text-ctex-blue uppercase tracking-tighter text-sm">
                    <Funnel weight="fill" className="text-ctex-orange" /> Filtros
                </h3>
                <button onClick={() => setIsFilterOpen(false)} className="lg:hidden p-2 bg-gray-100 rounded-full">
                    <X size={20} weight="bold" />
                </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 space-y-6 pb-32">
                <div>
                    <button onClick={() => setIsBrandsExpanded(!isBrandsExpanded)} className="flex items-center justify-between w-full mb-4 group">
                        <label className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-400 tracking-widest italic cursor-pointer group-hover:text-ctex-blue transition-colors font-bold">
                            <Buildings weight="bold" /> Marcas
                        </label>
                        <CaretDown size={14} className={`transition-transform duration-300 ${isBrandsExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`flex flex-col gap-1 overflow-hidden transition-all duration-300 ${isBrandsExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        {brands.map(brand => (
                            <button key={brand} onClick={() => handleBrandClick(brand)} className={`text-left px-4 py-2 rounded-xl text-xs transition-all flex justify-between items-center ${activeBrand === brand ? "bg-ctex-blue text-white font-bold shadow-md scale-[1.02]" : "text-gray-600 hover:bg-gray-50"}`}>
                                {brand}
                                <span className="text-[10px] opacity-40">{brand === "Todas" ? products.length : products.filter(p => p.brand === brand).length}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <button onClick={() => setIsCatsExpanded(!isCatsExpanded)} className="flex items-center justify-between w-full mb-4 group">
                        <label className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-400 tracking-widest italic cursor-pointer group-hover:text-ctex-orange transition-colors font-bold">
                            <Tag weight="bold" /> Categorías
                        </label>
                        <CaretDown size={14} className={`transition-transform duration-300 ${isCatsExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`flex flex-col gap-1 overflow-hidden transition-all duration-300 ${isCatsExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        {categoriesByBrand.map(sub => (
                            <button key={sub} onClick={() => handleSubcatClick(sub)} className={`text-left px-4 py-2 rounded-xl text-xs transition-all flex justify-between items-center ${activeSubcat === sub ? "bg-ctex-orange text-white font-bold shadow-lg scale-[1.02]" : "text-gray-600 hover:bg-gray-50"}`}>
                                <span className="truncate pr-2">{sub}</span>
                                <span className="text-[10px] opacity-40">{sub === "Todas" ? filteredByBrand.length : filteredByBrand.filter(p => p.subcategory === sub).length}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {(activeBrand !== "Todas" || activeSubcat !== "Todas") && (
                <div className="sticky bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-sm border-t">
                    <button onClick={() => {setActiveBrand("Todas"); setActiveSubcat("Todas");}} className="w-full flex items-center justify-center gap-2 text-xs font-bold text-red-500 hover:bg-red-50 p-3 rounded-xl transition-colors border border-red-100 shadow-sm">
                        <Trash weight="fill" /> Limpiar filtros
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <section className="container mx-auto px-4 py-12 pt-28 min-h-screen bg-gray-50 relative">
            <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`fixed bottom-24 right-6 z-50 p-4 bg-white text-ctex-blue rounded-full shadow-2xl border border-gray-100 transition-all duration-300 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}
            >
                <ArrowUp size={24} weight="bold" />
            </button>

            <button onClick={() => setIsFilterOpen(true)} className="lg:hidden fixed bottom-8 right-6 z-[60] bg-ctex-blue text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-3 font-bold active:scale-95 transition-transform"><Funnel size={22} weight="fill" /> Filtrar</button>
            <div className="flex flex-col lg:flex-row gap-8">
                <aside className="hidden lg:block w-72 sticky top-32 h-fit">
                    <FilterContent />
                </aside>

                {isFilterOpen && (
                    <div className="fixed inset-0 z-[100] lg:hidden bg-black/60 backdrop-blur-md flex justify-end p-4">
                        <div className="w-full max-w-xs animate-in slide-in-from-right duration-300">
                            <FilterContent />
                        </div>
                    </div>
                )}

                <div className="flex-1">
                    <div className="relative mb-8 group">
                        <MagnifyingGlass className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-ctex-blue" size={24} />
                        <input type="text" placeholder="¿Qué equipo necesitas hoy?" className="w-full pl-16 pr-6 py-5 rounded-[2rem] bg-white border-none shadow-sm focus:shadow-xl outline-none text-lg transition-all" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                        {filteredProducts.map(product => {
                            const isHighlighted = lastVisitedId === product.id.toString();
                            return (
                                <Link
                                    key={product.id}
                                    to={`/catalogo/${product.id}`}
                                    onClick={() => handleProductClick(product.id)}
                                    className={`group bg-white rounded-[2.5rem] overflow-hidden shadow-sm transition-all duration-500 border flex flex-col h-full ${
                                        isHighlighted
                                            ? "border-ctex-orange ring-4 ring-ctex-orange/10 scale-[1.03] z-10 shadow-2xl"
                                            : "border-gray-100 hover:shadow-2xl hover:border-ctex-blue/20"
                                    }`}
                                >
                                    <div className="h-64 bg-white relative flex items-center justify-center p-10 border-b border-gray-50 overflow-hidden">
                                        <img src={Array.isArray(product.image) ? product.image[0] : (product.image || "/img/placeholder.webp")} alt={product.name} loading="lazy" className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-700" onError={(e) => { e.target.src = "/img/placeholder.webp"; }} />
                                        <div className="absolute top-6 right-6 bg-gray-50 px-3 py-1 rounded-full text-[9px] font-black uppercase text-gray-400">{product.brand}</div>
                                    </div>
                                    <div className="p-8 flex flex-col flex-grow">
                                        <p className="text-ctex-orange text-[10px] font-black uppercase tracking-widest mb-2 italic">{product.subcategory}</p>
                                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-ctex-blue transition-colors mb-6 text-balance leading-tight">{product.name}</h3>
                                        <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
                                            <span className="text-xs font-bold text-gray-400 uppercase tracking-tighter">MOD: {product.id.split('-').pop()}</span>
                                            <span className={`font-black text-sm transition-transform group-hover:translate-x-1 ${isHighlighted ? 'text-ctex-orange' : 'text-ctex-blue'}`}>Ver Ficha →</span>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

const DesignIndustrialBlocks = ({ product }) => {
    const navigate = useNavigate();

    // ESTADOS
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [selectedImage, setSelectedImage] = useState(
        Array.isArray(product.image) ? product.image[0] : product.image
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
    const [isZoomed, setIsZoomed] = useState(false);

    // EFECTOS
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSelectedAccessories([]); // Reiniciar selección al cambiar de producto
        setSelectedImage(Array.isArray(product.image) ? product.image[0] : product.image);
    }, [product.id, product.image]);

    // LÓGICA DE ACCESORIOS
    const toggleAccessory = (accName) => {
        setSelectedAccessories(prev =>
            prev.includes(accName)
                ? prev.filter(item => item !== accName)
                : [...prev, accName]
        );
    };

    // GENERACIÓN DINÁMICA DEL LINK DE WHATSAPP
    const finalWhatsappLink = useMemo(() => {
        const accessoriesText = selectedAccessories.length > 0
            ? `\n\nTambién me interesa información de estos accesorios:\n${selectedAccessories.map(a => `- ${a}`).join('\n')}`
            : "";

        const message = `Buen día CTEX, solicito informes y cotización del equipo: ${product.name} (ID: ${product.id}). Me interesa saber sobre el equipo y la instalación.${accessoriesText}`;

        return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    }, [product, selectedAccessories]);

    // PRODUCTOS RELACIONADOS
    const relatedProducts = useMemo(() => {
        return products
            .filter(p => (p.brand === product.brand || p.subcategory === product.subcategory) && p.id !== product.id)
            .slice(0, 3);
    }, [product]);

    // MANEJO DE ZOOM E INTERACCIÓN
    const handleMouseMove = (e) => {
        if (!isZoomed) return;
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const x = ((e.pageX - left - window.scrollX) / width) * 100;
        const y = ((e.pageY - top - window.scrollY) / height) * 100;
        setZoomPos({ x, y });
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `CTEX - ${product.name}`,
                    text: `Mira este equipo: ${product.name}`,
                    url: window.location.href,
                });
            } catch (err) { console.log("Error al compartir", err); }
        }
    };

    return (
        <div className="container mx-auto px-4 py-12 pt-28">
            {/* CABECERA */}
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 border-b border-gray-100 pb-8 gap-6 animate-in fade-in duration-700">
                <div>
                    <span className="text-ctex-orange font-black text-[10px] uppercase tracking-[0.3em] mb-2 block italic underline decoration-ctex-orange/30 underline-offset-8">Ingeniería CTEX</span>
                    <h1 className="text-4xl md:text-5xl font-black text-ctex-blue tracking-tighter leading-none">{product.name}</h1>
                </div>
                <div className="flex gap-4">
                    <button onClick={handleShare} className="p-3 bg-gray-50 text-gray-400 hover:text-ctex-blue rounded-full transition-all border border-gray-100">
                        <ShareNetwork size={20} weight="bold" />
                    </button>
                    <button onClick={() => navigate(-1)} className="group text-[10px] font-black text-gray-400 hover:text-ctex-blue flex items-center gap-3 uppercase tracking-widest transition-all">
                        <ArrowLeft size={18} weight="bold" className="group-hover:-translate-x-1 transition-transform" /> Volver al catálogo
                    </button>
                </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-12 mb-24">
                {/* COLUMNA IZQUIERDA: IMÁGENES */}
                <div className="lg:col-span-5 space-y-6">
                    <div className="relative bg-white rounded-[3rem] border border-gray-100 aspect-square flex items-center justify-center overflow-hidden group cursor-pointer shadow-sm hover:shadow-xl transition-all duration-500 sticky top-32" onClick={() => { setIsModalOpen(true); setIsZoomed(false); }}>
                        <img key={selectedImage} src={selectedImage} alt={product.name} className="max-w-[85%] max-h-[85%] object-contain animate-in fade-in zoom-in-95 duration-500" />
                        <div className="absolute top-8 right-8 bg-ctex-blue text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg"><MagnifyingGlassPlus size={20} weight="bold" /></div>
                    </div>
                    {Array.isArray(product.image) && product.image.length > 1 && (
                        <div className="flex gap-4 justify-center flex-wrap animate-in slide-in-from-bottom duration-500">
                            {product.image.map((img, idx) => (
                                <button key={idx} onClick={() => setSelectedImage(img)} className={`w-20 h-20 rounded-2xl border-2 overflow-hidden transition-all ${selectedImage === img ? 'border-ctex-orange shadow-lg scale-110' : 'border-gray-100 opacity-50 hover:opacity-100'}`}>
                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* COLUMNA DERECHA: INFO Y ACCESORIOS */}
                <div className="lg:col-span-7 space-y-10">
                    <div className="bg-white rounded-[2.5rem] border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="bg-gray-50/50 px-8 py-5 border-b border-gray-100 flex items-center gap-3 font-black text-ctex-blue uppercase text-xs tracking-widest italic"><Info size={20} weight="bold" /> Ficha Técnica Oficial</div>
                        <div className="p-8 grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-12">
                            {product.details?.map((detail, i) => (
                                <div key={i} className="flex flex-col border-l-2 border-gray-50 pl-6 group">
                                    <p className="text-[9px] font-black text-gray-300 uppercase mb-1 tracking-widest group-hover:text-ctex-orange transition-colors">{detail.label}</p>
                                    <p className="text-base font-bold text-gray-700">{detail.value}</p>
                                </div>
                            ))}

                            {/* SELECTOR DE ACCESORIOS */}
                            {product.accessories && (
                                <div className="mt-8 p-6 bg-ctex-blue/5 rounded-[2rem] border-2 border-dashed border-ctex-blue/20 col-span-full">
                                    <p className="text-[10px] font-black text-ctex-blue uppercase tracking-[0.2em] mb-4 italic flex items-center gap-2">
                                        <Tag weight="fill" className="text-ctex-orange" /> Selecciona complementos para tu cotización:
                                    </p>
                                    <div className="grid grid-cols-1 gap-3">
                                        {product.accessories.map((acc, idx) => {
                                            const isSelected = selectedAccessories.includes(acc.name);
                                            return (
                                                <button
                                                    key={idx}
                                                    onClick={() => toggleAccessory(acc.name)}
                                                    className={`flex items-center justify-between p-3 rounded-xl transition-all border ${isSelected ? 'bg-ctex-blue text-white border-ctex-blue shadow-md scale-[1.02]' : 'bg-white text-gray-700 border-gray-100 hover:border-ctex-orange/50'}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-ctex-orange border-ctex-orange' : 'border-gray-300'}`}>
                                                            {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                        </div>
                                                        <span className="font-bold text-xs uppercase tracking-tight">{acc.name}</span>
                                                    </div>
                                                    {!isSelected && <span className="text-[9px] font-black opacity-40 italic">+ Añadir</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="mt-4 text-[9px] font-bold text-gray-400 italic">
                                        * Los accesorios seleccionados se añadirán a tu mensaje de asesoría.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* DESCRIPCIÓN Y BOTONES DE ACCIÓN */}
                    <div className="space-y-10 pl-4 animate-in slide-in-from-right duration-700">
                        <p className="text-lg text-gray-500 leading-relaxed font-medium italic border-l-4 border-ctex-orange/20 pl-6">{product.description}</p>

                        <div className="flex flex-col xl:flex-row gap-5 items-stretch">
                            <div className="flex flex-col gap-3 flex-1">
                                {product.manualPdf ? (
                                    Array.isArray(product.manualPdf) ? (
                                        product.manualPdf.map((pdf, idx) => (
                                            <a key={idx} href={pdf.url} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-3 border-2 border-ctex-blue text-ctex-blue hover:bg-ctex-blue hover:text-white py-4 rounded-2xl text-[11px] font-black uppercase tracking-[0.15em] transition-all shadow-sm group">
                                                <FilePdf size={20} weight="bold" /> {pdf.label || `Manual ${idx + 1}`}
                                            </a>
                                        ))
                                    ) : (
                                        <a href={product.manualPdf} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-3 border-2 border-ctex-blue text-ctex-blue hover:bg-ctex-blue hover:text-white py-4 rounded-2xl text-xs font-black uppercase tracking-[0.2em] transition-all shadow-sm group">
                                            <FilePdf size={22} weight="bold" /> Manual Técnico
                                        </a>
                                    )
                                ) : (
                                    <div className="w-full flex items-center justify-center bg-gray-50 text-gray-300 py-4 rounded-2xl text-[10px] font-black uppercase border border-dashed border-gray-200 cursor-not-allowed text-center">Sin Ficha Disponible</div>
                                )}
                            </div>

                            <a
                                href={finalWhatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-[1.3] flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-2xl text-xs font-black uppercase tracking-[0.2em] transition-all shadow-xl hover:translate-y-[-2px] active:scale-95"
                            >
                                <WhatsappLogo size={24} weight="fill" />
                                {selectedAccessories.length > 0 ? 'Solicitar Asesoría con Accesorios' : 'Solicitar Asesoría'}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* PRODUCTOS RELACIONADOS */}
            {relatedProducts.length > 0 && (
                <div className="mt-20 border-t border-gray-100 pt-16 animate-in fade-in duration-1000">
                    <h3 className="text-2xl font-black text-ctex-blue mb-10 uppercase tracking-tighter italic">Equipos similares</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {relatedProducts.map(rp => (
                            <Link key={rp.id} to={`/catalogo/${rp.id}`} className="group bg-white p-6 rounded-[2rem] border border-gray-50 shadow-sm hover:shadow-xl transition-all">
                                <div className="h-40 mb-4 flex items-center justify-center overflow-hidden">
                                    <img src={Array.isArray(rp.image) ? rp.image[0] : (rp.image || "/img/placeholder.webp")} loading="lazy" className="max-h-full object-contain group-hover:scale-110 transition-transform duration-500" alt={rp.name} />
                                </div>
                                <p className="text-[10px] font-black text-ctex-orange uppercase mb-1">{rp.brand}</p>
                                <h4 className="font-bold text-gray-900 text-sm leading-tight group-hover:text-ctex-blue transition-colors line-clamp-2">{rp.name}</h4>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* MODAL DE IMAGEN */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[1000] bg-black/98 backdrop-blur-2xl flex flex-col items-center justify-center p-4 animate-in fade-in duration-300 cursor-default" onClick={() => setIsModalOpen(false)}>
                    <button className="absolute top-8 right-8 text-white/40 hover:text-white z-50 transition-all hover:rotate-90">
                        <X size={44} weight="bold" />
                    </button>
                    <div className={`relative w-full h-full flex items-center justify-center overflow-hidden ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
                         onMouseMove={handleMouseMove}
                         onClick={(e) => { e.stopPropagation(); setIsZoomed(!isZoomed); }}>
                        <img src={selectedImage} alt="Visualizador" style={{ transformOrigin: isZoomed ? `${zoomPos.x}% ${zoomPos.y}%` : 'center', transform: isZoomed ? 'scale(3.2)' : 'scale(1)' }}
                             className="max-w-[85%] max-h-[85%] object-contain transition-transform duration-500 drop-shadow-2xl pointer-events-none" />
                    </div>
                </div>
            )}
        </div>
    );
};

const CataloguePage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();

    if (!productId) return <ProductList />;
    const productData = products.find(p => p.id.toString() === productId);

    if (!productData) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-center px-4 bg-gray-50">
                <h1 className="text-6xl font-black text-gray-200 mb-4 tracking-tighter italic uppercase">404</h1>
                <p className="text-gray-400 font-bold mb-8">El equipo solicitado ya no está en inventario.</p>
                <button onClick={() => navigate(-1)} className="px-8 py-3 bg-ctex-blue text-white rounded-full font-black text-xs uppercase tracking-widest shadow-xl active:scale-95 transition-transform">Regresar</button>
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen">
            <main>
                <DesignIndustrialBlocks product={productData} />
            </main>
        </div>
    );
};

export default CataloguePage;
